import React, {useEffect, useState} from "react";
import {Col, Row} from "reactstrap";
import {Divider} from "@mui/material";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Dialog from "../../../../../../components/base/dialog";
import Api from "../../../../../../../core/services/api/api";
import Button from "../../../../../../components/base/button/button";
import Chips, {ChipsTypes} from "../../../../../../components/base/chips/chips";
import {deepEqual} from "../../../../../../../core/services/utils/utils";
import {OutlinedDropdown} from "../../../../../../components/base/dropdown/outlined_dropdown";


const EditOperatorGroupsModal = ({open, setOpen, onSuccess, allGroups, operator: operatorProps}) => {
    const isMounted = useIsMounted();
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState(false);
    const [operator, setOperator] = useState();
    const [updatedOperator, setUpdatedOperator] = useState();

    /**
     * Listens for when the model open value or the operator changes.
     * If the operator exists and the modal is open, get the operator information from the server, otherwise set the
     * operator to null.
     */
    useEffect(() => {
        if (!open || !operatorProps) {
            setOperator(null);
            setUpdatedOperator(null);
        }
        if (open && operatorProps) {
            getOperator();
        }
    }, [open, operatorProps])


    /**
     * Gets the operator information from the server and if the component is not mounted, and modal open, and api
     * result is successful, then sets the operator.
     *
     * if the result of the api is not successful, closes the dialog as no updates would be possible
     */
    const getOperator = () => {
        setLoading(true);
        Api.getOperatorById(operatorProps.id).then((response) => {
            if (!isMounted() || !open) return;
            if (response?.resultFlag) {
                setOperator(response.data);
                setUpdatedOperator(response.data);
            } else {
                setOpen(false);
            }
            setLoading(false);
        })
    }

    /**
     * Edits the groups of an operator from the server. if the api result is successful, calls onSuccess and closes
     * the modal, otherwise does nothing.
     */
    const editOperatorGroups = () => {
        setUpdating(true);
        if (!updatedOperator) return;
        const forApi = {
            employeeship: {
                id: updatedOperator.employeeship.id,
                business: {
                    id: updatedOperator.employeeship.business.id,
                }
            },
            groups: updatedOperator.employeeship.usersGroups.map(e => ({id: e.id}))
        }
        Api.updateOperatorGroups(forApi).then((response) => {
            if (!isMounted() || !open) return;
            if (response?.resultFlag) {
                onSuccess(updatedOperator);
                setOpen(false);
            }
            setUpdating(false);
        })
    }

    /**
     * Removes a record from the operator.
     * @param group {any} the record to be removed
     */
    const onRemove = (group) => {
        setUpdatedOperator(previousState => ({
            ...previousState,
            employeeship: {
                ...(previousState?.employeeship ?? {}),
                usersGroups: previousState.employeeship?.usersGroups?.filter(e => e.id !== group.id),
            }
        }))
    }

    /**
     * Adds the selected group to the operator's groups
     */
    const onValueSelected = (value) => {
        setUpdatedOperator(previousState => ({
            ...previousState,
            employeeship: {
                ...(previousState?.employeeship ?? {}),
                usersGroups: [...(previousState?.employeeship?.usersGroups ?? []), value]
            }
        }))
    }


    return (
        <Dialog
            open={open}
            loading={updating}
            setOpen={setOpen}
        >
            <Row className='no-gutters align-items-center w-100 transition'>
                <Row xs={12} className='no-gutters justify-content-center m-3 w-100'>
                    <p className='text-lg text-color-1 font-weight-semi-bold text-center'>
                        {!operatorProps ? 'Edit Operator Groups' : `Edit ${operatorProps.firstName} ${operatorProps.lastName} Groups`}
                    </p>
                </Row>
                <Divider className='divider'/>
                <div className='my-3 w-100'/>
                <Row className='no-gutters w-100 px-1 align-items-start justify-content-start flex-wrap mx-3'>
                    {
                        [
                            ...updatedOperator?.employeeship?.usersGroups?.map(group =>
                                <div key={`${group.id}`} className='mb-2 mt-1 mr-2'>
                                    <Chips
                                        type={ChipsTypes.remove}
                                        content={group.name}
                                        onIconClick={() => onRemove(group)}
                                    />
                                </div>) ?? [],
                            <Col xs={6} md={4} key={`auto-complete-key`} className='mb-2'>
                                <OutlinedDropdown
                                    label={'New Group'}
                                    name={'newGroup'}
                                    value={undefined}
                                    onChange={value => onValueSelected(value)}
                                    data={[
                                        ...(allGroups?.filter(group => !((updatedOperator?.employeeship?.usersGroups?.map(t => t.id))?.includes(group.id)))?.map(e => ({
                                            value: e,
                                            label: e.name,
                                            id: e.id,
                                        })) ?? [])
                                    ]}
                                />
                                {/*<OutlinedAutoComplete*/}
                                {/*    value={autoCompleteValue ?? ""}*/}
                                {/*    loading={loading}*/}
                                {/*    label={'New Group'}*/}
                                {/*    onSelect={onSelectNewGroup}*/}
                                {/*    onChange={(e) => setAutoCompleteText(e.target.value)}*/}
                                {/*    clearOnBlur*/}
                                {/*    freeSolo={false}*/}
                                {/*    */}
                                {/*    type={'text'}*/}
                                {/*    variant={'outlined'}*/}
                                {/*    inputValue={autoCompleteText ?? ''}*/}
                                {/*    filterOptions={filterOptions}*/}
                                {/*    options={allGroups?.map(group => ({*/}
                                {/*        title: `${group.name}`,*/}
                                {/*        value: group,*/}
                                {/*    })) ?? []}*/}
                                {/*    renderOption={renderAutoCompleteGroup}*/}
                                {/*    loadingText={*/}
                                {/*        <div className='loading auto-complete'>*/}
                                {/*            <div/>*/}
                                {/*            <div/>*/}
                                {/*            <div/>*/}
                                {/*            <div/>*/}
                                {/*        </div>*/}
                                {/*    }*/}
                                {/*/>*/}
                            </Col>

                        ]
                    }
                </Row>
                <div className='my-3 w-100'/>
                <Divider className='divider'/>
                <Row className='no-gutters justify-content-between align-items-start w-100'>
                    <Button
                        disabled={!open || updating}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={() => setOpen(false)}
                        borderRadius={'0 8px 0 8px'}
                    >
                        {updating ? 'loading...' : 'Close'}
                    </Button>
                    <Button
                        disabled={!open || updating || deepEqual(operator, updatedOperator)}
                        variant={'dialog'}
                        color={'primary'}
                        onClick={editOperatorGroups}
                        borderRadius={'8px 0 8px 0'}
                    >
                        {updating || loading ? 'loading...' : 'Save Changes'}
                    </Button>
                </Row>
            </Row>
        </Dialog>
    );
}

export default EditOperatorGroupsModal;
